import { fetchWithTimeout, responseError, readJsonResponse, discardResponse } from './apiRequest';
import { withClientVersion } from './clientVersion';
import { workspaceVisible } from './workspaceVisibility';

const states = new Set(['queued', 'downloading', 'preparing', 'ready', 'failed']);
const validTask = task => task?.id && states.has(task.state);
const invalidResponse = () => Object.assign(new Error('The server returned an invalid import status.'), { code: 'malformed_response', termination_reason: 'parse' });
const attemptKey = uid => `instanttranscriber_import_attempt_v1:${uid}`;
const cancelled = signal => Object.assign(new Error('Import observation stopped.'), {
  name: 'AbortError', code: 'request_cancelled', termination_reason: signal?.reason === 'account_changed' ? 'account_changed' : 'view_closed',
});
function wait(signal, delay = 2000) {
  return new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); reject(cancelled(signal)); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, delay);
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
}
async function importId(uid, url) {
  let digest;
  try {
    digest = Array.from(new Uint8Array(await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(url))))
      .map(byte => byte.toString(16).padStart(2, '0')).join('');
    const saved = JSON.parse(localStorage.getItem(attemptKey(uid)) || 'null');
    if (saved?.digest === digest && saved.expires > Date.now()) return saved.id;
  } catch {}
  const id = window.crypto?.randomUUID?.() || '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, digit => {
    const random = window.crypto?.getRandomValues?.(new Uint8Array(1))[0] ?? Math.floor(Math.random() * 256);
    const mask = random & (15 >> (Number(digit) / 4));
    return (Number(digit) ^ mask).toString(16);
  });
  try { if (digest) localStorage.setItem(attemptKey(uid), JSON.stringify({ digest, id, expires: Date.now() + 86400000 })); } catch {}
  return id;
}
export function clearImportAttempt(uid) {
  try { localStorage.removeItem(attemptKey(uid)); } catch {}
}
async function sessionFor(uid, getSession, signal) {
  if (signal?.aborted) throw cancelled(signal);
  const session = await getSession();
  if (!session?.access_token || session.user?.id !== uid) throw Object.assign(cancelled(), { termination_reason: 'account_changed' });
  return session;
}
export const importOutcome = task => ({ task, payload: task.state === 'ready' ? task.result : task.error,
  response: { status: task.result_status || (task.state === 'ready' ? 200 : 500),
    ok: task.state === 'ready' && (task.result_status || 200) < 400 } });

export async function refreshImportOutcome(base, task, session, signal) {
  const res = await fetchWithTimeout(`${base}/imports/${task.id}`, { signal, requestPurpose:'import_resume',
    headers:{Authorization:`Bearer ${session.access_token}`} });
  const current = await readJsonResponse(res);
  if (!res.ok) throw responseError(res,current);
  if (!validTask(current)) throw invalidResponse();
  return importOutcome(current);
}

export async function watchImport(base, initial, uid, getSession, { signal, onState, onReconnect, onRecovered } = {}) {
  let task = initial;
  let failures = 0, interruptedAt = 0;
  while (true) {
    if (!validTask(task)) throw invalidResponse();
    onState?.(task);
    if (task.state === 'ready' || task.state === 'failed') {
      clearImportAttempt(uid);
      return importOutcome(task);
    }
    await wait(signal, Math.min(30000, 2000 * 2 ** Math.min(failures, 4)));
    if (!workspaceVisible()) continue;
    try {
      const session = await sessionFor(uid, getSession, signal);
      const res = await fetchWithTimeout(`${base}/imports/${task.id}`, { signal,
        requestPurpose:'import_watch',
        headers: { Authorization: `Bearer ${session.access_token}` } });
      const payload = await readJsonResponse(res);
      if (!res.ok) throw responseError(res, payload);
      if (!validTask(payload)) throw invalidResponse();
      task = payload;
      if (failures) onRecovered?.({ operation: 'import_watch', retry_count: failures, elapsed_ms: Date.now() - interruptedAt });
      failures = 0;
    } catch (error) {
      if (error?.code === 'request_cancelled' || [401,403,404,410].includes(error?.status)) throw error;
      // The task still belongs to the server; a network error is not an import failure.
      task = { ...task, reconnecting: true };
      onState?.(task);
      if (!failures) { interruptedAt = Date.now(); onReconnect?.(error, { operation: 'import_watch' }); }
      failures++;
    }
  }
}

export async function createImport(base, url, urlType, session, getSession, options = {}) {
  const id = await importId(session.user.id, url);
  const deadline = Date.now() + 30000;
  let res, payload;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const freshSession = await sessionFor(session.user.id, getSession, options.signal);
      res = await fetchWithTimeout(withClientVersion(`${base}/imports`), {
        signal: options.signal, method: 'POST', headers: { Authorization: `Bearer ${freshSession.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ file_url: url, import_id: id }),
      }, Math.max(1, Math.min(15000, deadline - Date.now())));
      if ([404,405].includes(res.status)) discardResponse(res);
      else {
        payload = await readJsonResponse(res);
        if (res.ok && !validTask(payload)) throw invalidResponse();
      }
      if (res.status === 503 && payload?.code === 'auth_verification_unavailable' && attempt < 2) {
        const raw = res.headers?.get?.('Retry-After');
        const seconds = Number(raw);
        const pause = Math.max(2000, raw ? (Number.isFinite(seconds) ? seconds*1000 : Date.parse(raw)-Date.now()) || 0 : 0);
        if (pause <= 10000 && Date.now()+pause < deadline) {
          if (!attempt) options.onReconnect?.(responseError(res,payload), { operation:'import_submit' });
          await wait(options.signal, pause);
          continue;
        }
      }
      if (attempt && res.ok) options.onRecovered?.({ operation: 'import_submit', retry_count: attempt });
      break;
    } catch (error) {
      if (error?.code === 'request_cancelled' || attempt === 2 || Date.now()+2000 >= deadline) throw error;
      if (!attempt) options.onReconnect?.(error, { operation: 'import_submit' });
      // Retry the same ID if the server accepted the POST but its reply was lost.
      await wait(options.signal);
    }
  }
  // Support a frontend/backend rolling deployment without changing the public API.
  if ([404,405].includes(res.status)) {
    const legacy = await fetchWithTimeout(withClientVersion(`${base}${urlType === 'youtube' ? '/youtube/ingest' : '/upload'}`), {
      signal: options.signal, method: 'POST', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_url: url }),
    }, 1800000);
    return { response: legacy, payload: await readJsonResponse(legacy) };
  }
  if (!res.ok) return { response: res, payload };
  return watchImport(base, payload, session.user.id, getSession, options);
}

export async function listImports(base, session, signal) {
  const res = await fetchWithTimeout(`${base}/imports`, { signal, requestPurpose:'import_list', headers: { Authorization: `Bearer ${session.access_token}` } });
  if ([404,405].includes(res.status)) { discardResponse(res); return []; }
  const payload = await readJsonResponse(res);
  if (!res.ok) throw responseError(res, payload);
  if (!Array.isArray(payload)) throw invalidResponse();
  return payload.filter(validTask);
}

export async function acknowledgeImport(base, task, session) {
  if (!task?.id) return;
  const res = await fetchWithTimeout(withClientVersion(`${base}/imports/${task.id}/acknowledge`), {
    method: 'POST', headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: '{}',
  });
  if (!res.ok) throw responseError(res, await readJsonResponse(res));
  discardResponse(res);
}
